import { ProductFormData } from '@/@types/Products';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  formatCurrency,
  getOfferCurrencyOptions,
  getPaymentFrequencyOptions,
  getPaymentTypeOptions,
  getTrialIntervalOptions,
  getTrialModeOptions,
} from '@/lib/select-options';
import React, { useEffect, useState } from 'react';
import {
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/Auth';

interface Step4Props {
  watch: UseFormWatch<ProductFormData>;
  errors: FieldErrors<ProductFormData>;
  setValue: UseFormSetValue<ProductFormData>;
  defaultValues: ProductFormData;
  handleBack: () => void;
  handleNext: () => void;
}

export default function Step4({
  watch,
  errors,
  setValue,
  defaultValues,
  handleBack,
  handleNext,
}: Step4Props) {
  const { t } = useTranslation(['common', 'onboarding']);
  const { authData } = useAuth();
  const canOfferRecurring =
    authData?.can_offer_recurring ??
    authData?.available_payment_methods?.some(
      (method) => method.supports_recurring
    ) ??
    false;
  const [formattedPrice, setFormattedPrice] = useState('');
  const [formattedTrialPrice, setFormattedTrialPrice] = useState('');

  const handlePriceChange = (e, setFormattedValue, fieldName) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const valueInCents = parseInt(rawValue || '0', 10);
    setFormattedValue(
      formatCurrency(valueInCents / 100, defaultValues?.offer_currency)
    );
    setValue(fieldName, valueInCents);
  };

  const resetTrialFields = () => {
    setValue('offer_trial_enabled', false);
    setValue('offer_trial_mode', '');
    setValue('offer_trial_price', null);
    setValue('offer_trial_interval', '');
    setFormattedTrialPrice('');
  };

  useEffect(() => {
    if (defaultValues?.offer_currency) {
      setFormattedPrice(
        formatCurrency(watch('offer_price') / 100, defaultValues.offer_currency)
      );
      const trialPrice = watch('offer_trial_price');
      if (trialPrice) {
        setFormattedTrialPrice(
          formatCurrency(trialPrice / 100, defaultValues.offer_currency)
        );
      }
    }
  }, [watch('offer_currency')]);

  useEffect(() => {
    if (watch('offer_payment_type') === 'single') {
      setValue('offer_recurring_interval', 'monthly');
    }
  }, [watch('offer_payment_type'), setValue]);

  useEffect(() => {
    if (watch('offer_payment_type') !== 'recurring') {
      resetTrialFields();
      return;
    }

    if (defaultValues?.offer_currency !== 'BRL') {
      setValue('offer_currency', 'BRL');
      setFormattedPrice(
        formatCurrency(watch('offer_price') / 100, 'BRL')
      );
    }
  }, [
    watch('offer_payment_type'),
    defaultValues?.offer_currency,
    setValue,
    watch,
  ]);

  const trialEnabled = !!defaultValues?.offer_trial_enabled;
  const trialMode = defaultValues?.offer_trial_mode;
  const trialInterval = defaultValues?.offer_trial_interval;
  const trialDurationHint =
    trialInterval &&
    t('trialDurationHint', {
      unit: t(`trialDurationUnit.${trialInterval}`),
    });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleNext();
      }}
      className='w-full max-w-lg'
    >
      <h1 className='text-2xl font-bold text-center mb-6'>
        {t('step4Title')}
        <p className='text-interactive'>{t('payment')}</p>{' '}
        {t('of_your_product')}
      </h1>
      <section className='space-y-6'>
        <CustomSelect
          placeholder={t('paymentTypePlaceholder')}
          options={getPaymentTypeOptions(t, canOfferRecurring)}
          label={t('paymentTypeLabel')}
          defaultValue={defaultValues?.offer_payment_type}
          value={defaultValues?.offer_payment_type}
          onValueChange={(value) => setValue('offer_payment_type', value)}
          helperText={errors?.offer_payment_type?.message}
          data-cy='payment-type'
          id='paymentType'
        />

        {defaultValues?.offer_payment_type === 'recurring' && (
          <>
            <CustomSelect
              placeholder={t('recurrenceIntervalPlaceholder')}
              onValueChange={(value) => {
                setValue('offer_recurring_interval', value);
                const currentTrial = watch('offer_trial_interval');
                const allowed = getTrialIntervalOptions(t, value);
                if (
                  currentTrial &&
                  !allowed.some((option) => option.value === currentTrial)
                ) {
                  setValue('offer_trial_interval', '');
                }
              }}
              defaultValue={defaultValues?.offer_recurring_interval}
              value={defaultValues?.offer_recurring_interval}
              label={t('recurrenceIntervalLabel')}
              options={getPaymentFrequencyOptions(t)}
              helperText={errors?.offer_recurring_interval?.message}
              id='recurrenceInterval'
            />

            <div className='flex flex-col gap-3'>
              <label
                htmlFor='offer_trial_enabled'
                className='text-sm font-medium text-interactive'
              >
                {t('trialEnabledLabel')}
              </label>
              <Switch
                id='offer_trial_enabled'
                checked={trialEnabled}
                onCheckedChange={(checked) => {
                  setValue('offer_trial_enabled', checked);
                  if (!checked) {
                    setValue('offer_trial_mode', '');
                    setValue('offer_trial_price', null);
                    setValue('offer_trial_interval', '');
                    setFormattedTrialPrice('');
                  }
                }}
                data-cy='offer-trial-enabled'
              />
            </div>

            {trialEnabled && (
              <div className='space-y-4 rounded-xl border border-colorBorder p-4'>
                <CustomSelect
                  placeholder={t('trialModePlaceholder')}
                  options={getTrialModeOptions(t)}
                  label={t('trialModeLabel')}
                  value={trialMode || ''}
                  onValueChange={(value) => {
                    setValue('offer_trial_mode', value);
                    if (value !== 'promotional') {
                      setValue('offer_trial_price', null);
                      setFormattedTrialPrice('');
                    }
                  }}
                  helperText={errors?.offer_trial_mode?.message}
                  id='trialMode'
                />
                {trialMode === 'free' && (
                  <p className='text-xs text-muted'>{t('trialModeHintFree')}</p>
                )}
                {trialMode === 'promotional' && (
                  <p className='text-xs text-muted'>
                    {t('trialModeHintPromotional')}
                  </p>
                )}

                {trialMode === 'promotional' && (
                  <div className='flex gap-4 items-end'>
                    <Input
                      label={t('trialPriceLabel')}
                      placeholder={t('0,00')}
                      value={formattedTrialPrice}
                      onChange={(e) =>
                        handlePriceChange(
                          e,
                          setFormattedTrialPrice,
                          'offer_trial_price'
                        )
                      }
                      className='flex-1'
                      helperText={errors?.offer_trial_price?.message}
                      data-cy='offer-trial-price'
                      hasError={!!errors?.offer_trial_price}
                    />
                    <div
                      className={`w-[100px] ${errors?.offer_trial_price ? 'mb-7' : ''}`}
                    >
                      <CustomSelect
                        value={defaultValues?.offer_currency}
                        label=''
                        placeholder=''
                        onValueChange={(value) =>
                          setValue('offer_currency', value)
                        }
                        options={getOfferCurrencyOptions(
                          defaultValues?.offer_payment_type
                        )}
                        className='w-[100px]'
                        id='trialCurrencySelect'
                      />
                    </div>
                  </div>
                )}

                <CustomSelect
                  placeholder={t('trialIntervalPlaceholder')}
                  options={getTrialIntervalOptions(
                    t,
                    defaultValues?.offer_recurring_interval,
                  )}
                  label={t('trialIntervalLabel')}
                  value={trialInterval || ''}
                  onValueChange={(value) =>
                    setValue('offer_trial_interval', value)
                  }
                  helperText={errors?.offer_trial_interval?.message}
                  id='trialInterval'
                />
                {trialDurationHint && !errors?.offer_trial_interval?.message && (
                  <p className='text-xs text-muted'>{trialDurationHint}</p>
                )}
              </div>
            )}
          </>
        )}
        <div className='flex gap-4 items-end'>
          <Input
            label={t('offerPriceLabel')}
            placeholder={t('0,00')}
            value={formattedPrice}
            onChange={(e) =>
              handlePriceChange(e, setFormattedPrice, 'offer_price')
            }
            className='flex-1'
            helperText={errors?.offer_price?.message}
            data-cy='offer-price'
            hasError={!!errors?.offer_price}
          />

          <div className={`w-[100px] ${errors?.offer_price ? 'mb-7' : ''}`}>
            <CustomSelect
              value={defaultValues?.offer_currency}
              label=''
              placeholder=''
              onValueChange={(value) => setValue('offer_currency', value)}
              options={getOfferCurrencyOptions(defaultValues?.offer_payment_type)}
              className='w-[100px]'
              id='currencySelect'
            />
          </div>
        </div>
      </section>
      <div className='flex justify-center gap-6 mt-8'>
        <Button type='button' onClick={handleBack} variant='ghost'>
          {t('back')}
        </Button>
        <Button data-cy='next' type='submit' variant='default'>
          {t('next')}
        </Button>
      </div>
    </form>
  );
}
